import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Tooltip,
  Progress,
  Modal,
  message,
  Popconfirm,
  Dropdown,
  Menu,
} from 'antd';
import {
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  ExportOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { DATA_TYPES, EXPORT_STATUS } from '../constants';
import type { ExportHistory as ExportHistoryType, ExportStatus, DataType } from '../types';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ExportHistory: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [exportHistory, setExportHistory] = useState<ExportHistoryType[]>([]);
  const [filteredData, setFilteredData] = useState<ExportHistoryType[]>([]);
  const [filters, setFilters] = useState({
    search: '',
    status: undefined as ExportStatus | undefined,
    dataType: undefined as DataType | undefined,
    dateRange: null as [string, string] | null,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedExport, setSelectedExport] = useState<ExportHistoryType | null>(null);

  // Mock data for export history
  const mockExportHistory: ExportHistoryType[] = [
    {
      id: 'exp_001',
      configId: 'config_001',
      configName: 'User Data Export - Daily',
      dataType: 'users',
      format: 'csv',
      status: 'completed',
      fileSize: 2048576, // 2MB
      recordCount: 1500,
      downloadUrl: '/downloads/users_2024_01_25.csv',
      createdBy: 'admin@fitui.com',
      createdAt: '2024-01-25T10:30:00Z',
      completedAt: '2024-01-25T10:32:15Z',
    },
    {
      id: 'exp_002',
      configId: 'config_002',
      configName: 'Client Programs Export',
      dataType: 'programs',
      format: 'excel',
      status: 'completed',
      fileSize: 5242880, // 5MB
      recordCount: 850,
      downloadUrl: '/downloads/programs_2024_01_25.xlsx',
      createdBy: 'coach@fitui.com',
      createdAt: '2024-01-25T09:15:00Z',
      completedAt: '2024-01-25T09:18:30Z',
    },
    {
      id: 'exp_003',
      configId: 'config_003',
      configName: 'Assessment Data Export',
      dataType: 'assessments',
      format: 'json',
      status: 'processing',
      recordCount: 2300,
      createdBy: 'analyst@fitui.com',
      createdAt: '2024-01-25T11:00:00Z',
    },
    {
      id: 'exp_004',
      configId: 'config_004',
      configName: 'Exercise Library Export',
      dataType: 'exercises',
      format: 'pdf',
      status: 'failed',
      createdBy: 'admin@fitui.com',
      createdAt: '2024-01-25T08:45:00Z',
      error: 'Failed to generate PDF: Template not found',
    },
    {
      id: 'exp_005',
      configId: 'config_005',
      configName: 'Nutrition Data Export',
      dataType: 'nutrition',
      format: 'csv',
      status: 'cancelled',
      createdBy: 'nutritionist@fitui.com',
      createdAt: '2024-01-24T16:20:00Z',
    },
  ];

  // Load data on component mount
  useEffect(() => {
    loadExportHistory();
  }, []);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [exportHistory, filters]);

  const loadExportHistory = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setExportHistory(mockExportHistory);
    } catch (error) {
      message.error('Failed to load export history');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...exportHistory];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item =>
        item.configName.toLowerCase().includes(searchLower) ||
        item.createdBy.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(item => item.status === filters.status);
    }

    // Data type filter
    if (filters.dataType) {
      filtered = filtered.filter(item => item.dataType === filters.dataType);
    }

    // Date range filter
    if (filters.dateRange) {
      const [start, end] = filters.dateRange;
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.createdAt);
        return itemDate >= new Date(start) && itemDate <= new Date(end);
      });
    }

    setFilteredData(filtered);
  };

  const handleDownload = (record: ExportHistoryType) => {
    if (record.downloadUrl) {
      // Simulate download
      message.success(`Downloading ${record.configName}...`);
      // In real implementation: window.open(record.downloadUrl, '_blank');
    } else {
      message.error('Download not available for this export');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setExportHistory(prev => prev.filter(item => item.id !== id));
      message.success('Export history deleted successfully');
    } catch (error) {
      message.error('Failed to delete export history');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setExportHistory(prev => prev.filter(item => !selectedRowKeys.includes(item.id)));
      setSelectedRowKeys([]);
      message.success(`Deleted ${selectedRowKeys.length} export records`);
    } catch (error) {
      message.error('Failed to delete export records');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  const getStatusTag = (status: ExportStatus) => {
    const statusConfig = EXPORT_STATUS.find(s => s.value === status);
    return (
      <Tag color={statusConfig?.color}>
        {statusConfig?.label || status}
      </Tag>
    );
  };

  const columns: ColumnsType<ExportHistoryType> = [
    {
      title: 'Export Configuration',
      dataIndex: 'configName',
      key: 'configName',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {DATA_TYPES.find(t => t.value === record.dataType)?.label} • {record.format.toUpperCase()}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      filters: EXPORT_STATUS.map(s => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Records',
      dataIndex: 'recordCount',
      key: 'recordCount',
      render: (count) => count ? count.toLocaleString() : '-',
      align: 'right',
    },
    {
      title: 'File Size',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: formatFileSize,
      align: 'right',
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (email) => (
        <Text style={{ fontSize: 12 }}>{email}</Text>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'View Details',
            onClick: () => {
              setSelectedExport(record);
              setDetailModalVisible(true);
            },
          },
          ...(record.downloadUrl ? [{
            key: 'download',
            icon: <DownloadOutlined />,
            label: 'Download',
            onClick: () => handleDownload(record),
          }] : []),
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: 'Delete',
            danger: true,
            onClick: () => handleDelete(record.id),
          },
        ];

        return (
          <Space>
            {record.downloadUrl && (
              <Tooltip title="Download">
                <Button
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(record)}
                />
              </Tooltip>
            )}
            
            <Dropdown
              menu={{
                items: menuItems,
                onClick: ({ key }) => {
                  const item = menuItems.find(i => i.key === key);
                  if (item && item.onClick) {
                    item.onClick();
                  }
                },
              }}
              trigger={['click']}
            >
              <Button type="text" size="small" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys as string[]);
    },
    getCheckboxProps: (record: ExportHistoryType) => ({
      disabled: record.status === 'processing',
    }),
  };

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={3}>Export History</Title>
            <Text type="secondary">
              View and manage your data export history.
            </Text>
          </Col>
          
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadExportHistory}
                loading={loading}
              >
                Refresh
              </Button>
              
              {selectedRowKeys.length > 0 && (
                <Popconfirm
                  title={`Delete ${selectedRowKeys.length} selected exports?`}
                  description="This action cannot be undone."
                  onConfirm={handleBulkDelete}
                  okText="Delete"
                  okType="danger"
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={loading}
                  >
                    Delete Selected ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Search
              placeholder="Search by name or creator..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              style={{ width: '100%' }}
            />
          </Col>
          
          <Col span={4}>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              {EXPORT_STATUS.map(status => (
                <Option key={status.value} value={status.value}>
                  {status.label}
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col span={4}>
            <Select
              placeholder="Data Type"
              value={filters.dataType}
              onChange={(value) => setFilters(prev => ({ ...prev, dataType: value }))}
              style={{ width: '100%' }}
              allowClear
            >
              {DATA_TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Col>
          
          <Col span={8}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates, dateStrings) => {
                setFilters(prev => ({
                  ...prev,
                  dateRange: dates ? [dateStrings[0], dateStrings[1]] : null,
                }));
              }}
            />
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} exports`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Export Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          ...(selectedExport?.downloadUrl ? [
            <Button
              key="download"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                if (selectedExport) handleDownload(selectedExport);
                setDetailModalVisible(false);
              }}
            >
              Download
            </Button>
          ] : []),
        ]}
        width={600}
      >
        {selectedExport && (
          <div style={{ fontSize: 14 }}>
            <Row gutter={[16, 8]}>
              <Col span={8}><strong>Configuration:</strong></Col>
              <Col span={16}>{selectedExport.configName}</Col>
              
              <Col span={8}><strong>Data Type:</strong></Col>
              <Col span={16}>
                {DATA_TYPES.find(t => t.value === selectedExport.dataType)?.label}
              </Col>
              
              <Col span={8}><strong>Format:</strong></Col>
              <Col span={16}>{selectedExport.format.toUpperCase()}</Col>
              
              <Col span={8}><strong>Status:</strong></Col>
              <Col span={16}>{getStatusTag(selectedExport.status)}</Col>
              
              <Col span={8}><strong>Records:</strong></Col>
              <Col span={16}>
                {selectedExport.recordCount ? selectedExport.recordCount.toLocaleString() : '-'}
              </Col>
              
              <Col span={8}><strong>File Size:</strong></Col>
              <Col span={16}>{formatFileSize(selectedExport.fileSize)}</Col>
              
              <Col span={8}><strong>Created By:</strong></Col>
              <Col span={16}>{selectedExport.createdBy}</Col>
              
              <Col span={8}><strong>Created At:</strong></Col>
              <Col span={16}>{new Date(selectedExport.createdAt).toLocaleString()}</Col>
              
              {selectedExport.completedAt && (
                <>
                  <Col span={8}><strong>Completed At:</strong></Col>
                  <Col span={16}>{new Date(selectedExport.completedAt).toLocaleString()}</Col>
                </>
              )}
              
              {selectedExport.error && (
                <>
                  <Col span={8}><strong>Error:</strong></Col>
                  <Col span={16}>
                    <Text type="danger">{selectedExport.error}</Text>
                  </Col>
                </>
              )}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ExportHistory; 